import type {ReactElement, ReactEventHandler, SyntheticEvent} from 'react';
import {useRef} from 'react';
import styles from './design-page-selector.module.scss';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {ClickableDiv} from '@Components/clickable-div';
import {noop} from '@Utils/general.util';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {SkeletonBrick} from '@Components/skeleton-brick';
import type {PanelContentProps, PanelFooterProps, PanelHeaderProps} from '@Components/panel';
import {HeaderSize, Panel, PanelContent, PanelFooter, PanelHeader} from '@Components/panel';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {DesignPageSelectorOpts} from './design-page-selector.types';
import {PageSelectorSize} from './design-page-selector.types';
import {Image} from 'react-bootstrap';
import {getAssetUrl} from '@Utils/s3.util';
import {SkeletonLoader, SkeletonLoaderVariant} from '@Components/skeleton-loader';
import {useDesignPageSelector} from './useDesignPageSelector';

const S3_PLACEHOLDER_FILE_NAME = 'placeholder-image.png';

export interface DesignPageData {
  pageIds: string[];
  previewUrls: Record<string, string>;
}

export interface DesignPageSelectorProps {
  posterId: string;
  onSubmit?: (selectedPageIds: string[]) => void;
  size?: PageSelectorSize;

  pageData?: DesignPageData;
  selectionOpts?: DesignPageSelectorOpts;
  className?: string;
  ctaText?: string;

  headerTitle?: string;
  showBackButton?: boolean;
  onBackButton?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
}

export function DesignPageSelector({
  posterId,
  className = '',
  ctaText = window.i18next.t('pmwjs_publish'),
  onSubmit = noop,
  selectionOpts = {},
  pageData,
  headerTitle = window.i18next.t('pmwjs_select_a_page'),
  showBackButton = false,
  onBackButton = noop,
  showCloseButton = false,
  onClose = noop,
  size = PageSelectorSize.MEDIUM,
}: DesignPageSelectorProps): ReactElement<DesignPageSelectorProps> | null {
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const {topShadow, bottomShadow} = useTopBottomScrollShadow(scrollableContainerRef);

  const {pageIds, pagePreviewUrls, isLoading, selectedPageIds, handlePageClick, isPageSelected, isSelectionDisabled, isFooterButtonDisabled} =
    useDesignPageSelector({posterId, pageData, selectionOpts});

  const handleFooterButtonClick = (): void => {
    if (!isFooterButtonDisabled()) {
      onSubmit(Array.from(selectedPageIds));
    }
  };

  const handleImageSourceError: ReactEventHandler = (e: SyntheticEvent<HTMLImageElement>) => {
    const eventTarget = e.target as HTMLImageElement;

    eventTarget.onerror = null;
    eventTarget.src = getAssetUrl(S3_PLACEHOLDER_FILE_NAME);
  };

  const getPagesForGrid = (gridPageIds: string[]): ReactElement => {
    return (
      <>
        {gridPageIds.map((pageId, index) => {
          const isSelected = isPageSelected(pageId);
          const isDisabled = isSelectionDisabled(pageId);
          const previewURL = pagePreviewUrls[pageId];
          const isGridItemLoading = isLoading || !previewURL;
          const gridTextSize = size === PageSelectorSize.MEDIUM ? styles.mediumSize : '';

          return (
            <div key={index} className={styles.gridItem}>
              <ClickableDiv
                className={`${styles.gridItemImage} ${isSelected ? styles.gridItemSelected : ''} ${isDisabled ? styles.gridItemDisabled : ''}`}
                onClick={(): void => {
                  handlePageClick(pageId);
                }}
                aria-disabled={isDisabled}
              >
                <SkeletonBrick className={`${styles.gridImage} ${isGridItemLoading ? '' : styles.invisibleGridImage}`} />
                <Image
                  onError={handleImageSourceError}
                  src={previewURL}
                  alt={`Page ${index + 1}`}
                  className={`${styles.gridImage} ${isGridItemLoading ? styles.invisibleGridImage : ''}`}
                />
              </ClickableDiv>
              <div className={`${styles.gridItemText} ${gridTextSize} content-primary ${isSelected ? styles.selectedText : ''} ${isDisabled ? styles.disabledText : ''}`}>
                {window.i18next.t('pmwjs_page_number', {pageNumber: `${index + 1}`})}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const getPanelHeader = (): ReactElement<PanelHeaderProps> => {
    let titleClasses: string;
    let backButtonIconSize: IconSize | undefined = undefined;

    if (size === PageSelectorSize.SMALL) {
      titleClasses = 'body-md-bold';
      backButtonIconSize = IconSize.SIZE_ICON_16;
    } else {
      titleClasses = 'body-lg-bold';
    }

    return (
      <PanelHeader
        title={headerTitle}
        showBackButton={showBackButton}
        onBackButton={onBackButton}
        showCloseButton={showCloseButton}
        onClose={onClose}
        size={HeaderSize.SMALL}
        backButtonIconSize={backButtonIconSize}
        titleClasses={titleClasses}
        className={`_no-border-bottom spacing-p-3 ${styles.panelHeader}`}
      />
    );
  };

  const getPanelContent = (): ReactElement<PanelContentProps> => {
    const gridLoaderDimensions = size === PageSelectorSize.MEDIUM ? '100px' : '80px';
    const gridSizeClass = size === PageSelectorSize.MEDIUM ? styles.mediumSize : '';
    const panelContentSizeClass = size === PageSelectorSize.MEDIUM ? styles.mediumSize : '';
    let content: ReactElement;

    if (isLoading) {
      content = (
        <SkeletonLoader
          isAnimated
          isPulsating
          variant={SkeletonLoaderVariant.GRID}
          options={{itemCount: 3, columns: 3, gridItemHeight: gridLoaderDimensions, gridItemWidth: gridLoaderDimensions}}
        />
      );
    } else {
      content = getPagesForGrid(pageIds);
    }

    return (
      <PanelContent className={`${styles.panelContent} ${panelContentSizeClass}`}>
        <div className={`${styles.gridContainer} ${topShadow ? styles.contentTopShadow : ''} ${bottomShadow ? styles.contentBottomShadow : ''}`}>
          <div ref={scrollableContainerRef} className={styles.scrollableWrapper}>
            <div className={`${styles.grid} ${gridSizeClass}`}>{content}</div>
          </div>
        </div>
      </PanelContent>
    );
  };

  const getPanelFooter = (): ReactElement<PanelFooterProps> => {
    return (
      <PanelFooter
        className={styles.panelFooter}
        primaryActions={[
          <Button
            key="submit-button"
            text={ctaText}
            onClick={handleFooterButtonClick}
            disabled={isFooterButtonDisabled()}
            icon="icon-publish-arrow"
            iconPlacement={IconPlacement.RIGHT}
            type={Type.PRIMARY}
            size={Size.SMALL}
            isFullWidth
          />,
        ]}
        fullWidthPrimaryActions
      />
    );
  };

  return (
    <Panel
      panelBodyClassName={styles.panelBodyClassName}
      className={`${styles.panel} ${className}`}
      panelHeader={getPanelHeader()}
      panelContent={getPanelContent()}
      panelFooter={getPanelFooter()}
    />
  );
}
